import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@mlm/ui';
import {
  listShiftCloses,
  shiftClose,
  updateShiftCloseNotes,
  type ShiftCloseResult,
  type ShiftCloseRow,
} from '../lib/api';
import { useAuthStore, useHasPerm } from '../lib/auth-store';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';

function startOfTodayLocal(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // datetime-local wants `YYYY-MM-DDTHH:mm` in local time.
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

/**
 * End of Shift (feature 16). The cashier sets the shift start, counts the
 * drawer, and submits; the server computes expected cash since `startAt` and
 * stores the variance. The resulting record is printable via `.receipt-tape`.
 */
export function EndShiftPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const [startAt, setStartAt] = useState(startOfTodayLocal());
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<ShiftCloseResult | null>(null);

  // Amending a closed shift's note is supervisor authority. Holders also see
  // OTHER cashiers' records (the server widens the list for them), so the
  // query key carries the perm — a re-login that grants it must refetch.
  const canEditMemo = useHasPerm('pos.memo.edit');
  const history = useQuery({
    queryKey: ['shift-closes', branchId, user?.id, canEditMemo],
    enabled: !!user?.id,
    queryFn: () => listShiftCloses({ branchId: branchId ?? undefined, limit: 20 }),
  });

  const close = useMutation({
    mutationFn: () =>
      shiftClose({
        branchId: branchId!,
        startAt: new Date(startAt).toISOString(),
        actualCash: Number(actualCash),
        notes: notes || undefined,
      }),
    meta: { successMessage: 'Shift closed.' },
    onSuccess: (res) => {
      setResult(res);
      setActualCash('');
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['shift-closes'] });
    },
  });

  const branchName = branches.find((b) => b.id === branchId)?.name ?? '';
  const canSubmit = !!branchId && actualCash !== '' && Number(actualCash) >= 0 && !close.isPending;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="no-print flex flex-row items-center justify-between">
          <CardTitle>End of shift</CardTitle>
          <BranchPicker branches={branches} branchId={branchId} isScoped={isScoped} onChange={setSelectedBranchId} />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="no-print grid max-w-md gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Shift start</label>
              <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Counted cash (₱)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={actualCash}
                onChange={(e) => setActualCash(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Notes (optional)</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => close.mutate()} disabled={!canSubmit}>
                {close.isPending ? 'Closing…' : 'Close shift'}
              </Button>
              {close.isError && <span className="text-sm text-destructive">Failed to close shift.</span>}
            </div>
          </div>

          {result && (
            <div className="receipt-tape font-mono text-sm space-y-2 max-w-md border-t pt-4">
              <div className="text-center space-y-0.5 pb-2 border-b border-dashed">
                <div className="font-semibold text-base">End of Shift</div>
                <div className="text-xs">{branchName}</div>
                <div className="text-xs">{user?.email}</div>
                <div className="text-xs">{new Date(result.record.endAt).toLocaleString()}</div>
              </div>
              <Row label="Shift start" value={new Date(result.record.startAt).toLocaleString()} />
              <Row label="Expected cash" value={`₱${Number(result.expectedCash).toFixed(2)}`} />
              <Row label="Counted cash" value={`₱${Number(result.record.actualCash).toFixed(2)}`} />
              <Row
                label="Variance"
                value={`₱${Number(result.variance).toFixed(2)}`}
                bold
              />
              {result.record.notes && <div className="text-xs pt-1">{result.record.notes}</div>}
              <div className="no-print pt-2">
                <Button size="sm" variant="outline" onClick={() => window.print()}>
                  Print
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="no-print">
        <CardHeader>
          <CardTitle>Recent shift closes</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !history.data?.length ? (
            <p className="text-sm text-muted-foreground">No shift records yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-2 py-1.5">Closed</th>
                  <th className="px-2 py-1.5">Cashier</th>
                  <th className="px-2 py-1.5 text-right">Expected</th>
                  <th className="px-2 py-1.5 text-right">Counted</th>
                  <th className="px-2 py-1.5 text-right">Variance</th>
                  <th className="px-2 py-1.5">Notes</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-2 py-1.5 text-xs text-muted-foreground">
                      {new Date(s.endAt).toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-xs">{s.cashierEmail ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">₱{Number(s.expectedCash).toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">₱{Number(s.actualCash).toFixed(2)}</td>
                    <td
                      className={`px-2 py-1.5 text-right tabular-nums font-medium ${
                        Number(s.variance) < 0 ? 'text-destructive' : Number(s.variance) > 0 ? 'text-warning' : ''
                      }`}
                    >
                      ₱{Number(s.variance).toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5">
                      <ShiftNotesCell record={s} canEdit={canEditMemo} onSaved={() => history.refetch()} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * A closed shift's note — read-only, or inline-editable for a `pos.memo.edit`
 * holder. The counted cash / expected / variance are the record and are never
 * touched here; only the operator's commentary is amendable.
 */
function ShiftNotesCell({
  record,
  canEdit,
  onSaved,
}: {
  record: ShiftCloseRow;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(record.notes ?? '');
  const save = useMutation({
    mutationFn: (notes: string) =>
      updateShiftCloseNotes(record.id, notes.trim() ? notes.trim() : null),
    meta: { successMessage: 'Shift note saved.' },
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-7 min-w-[12rem] text-xs"
          maxLength={1000}
          placeholder="Note (blank to clear)"
        />
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(draft)}>
          {save.isPending ? '…' : 'Save'}
        </Button>
        <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 text-xs">
        <span className="whitespace-pre-wrap">
          {record.notes ?? <span className="text-muted-foreground">—</span>}
        </span>
        {record.notesUpdatedAt && (
          <div className="text-[11px] text-muted-foreground">
            edited{record.notesUpdatedByName ? ` by ${record.notesUpdatedByName}` : ''}
          </div>
        )}
      </div>
      {canEdit && (
        <button
          type="button"
          className="flex-none text-[11px] text-muted-foreground underline hover:text-foreground"
          onClick={() => {
            setDraft(record.notes ?? '');
            setEditing(true);
          }}
        >
          {record.notes ? 'Edit' : 'Add'}
        </button>
      )}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold' : ''}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
